// Hook pour gérer l'état du formulaire d'édition/création d'un type de licence
import { useState, useCallback } from 'react';
import { LicenseType } from '../../../types/database';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

export interface LicenseTypeFormData {
  name: string;
  description: string;
  price: number;
  duration_days: number | undefined;
  max_devices: number;
  features: Record<string, boolean | string | number>;
  is_active: boolean;
}

const initialFormData: LicenseTypeFormData = {
  name: '',
  description: '',
  price: 0,
  duration_days: undefined,
  max_devices: 1,
  features: {},
  is_active: true
};

export function useLicenseTypeForm(onSuccess?: () => void) {
  const [editingType, setEditingType] = useState<LicenseType | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<LicenseTypeFormData>(initialFormData);
  const [saving, setSaving] = useState(false);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setEditingType(null);
    setIsCreating(false);
  }, []);

  const startEdit = useCallback((licenseType: LicenseType) => {
    setFormData({
      name: licenseType.name,
      description: licenseType.description || '',
      price: licenseType.price,
      duration_days: licenseType.duration_days || undefined,
      max_devices: licenseType.max_devices,
      features: licenseType.features || {},
      is_active: licenseType.is_active
    });
    setEditingType(licenseType);
    setIsCreating(false);
  }, []);

  const startCreate = useCallback(() => {
    resetForm();
    setIsCreating(true);
  }, [resetForm]);

  const handleSave = useCallback(async () => {
    if (!formData.name.trim() || formData.price < 0) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSaving(true);

      const dataToSave = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        price: formData.price,
        duration_days: formData.duration_days || null,
        max_devices: formData.max_devices,
        features: formData.features,
        is_active: formData.is_active
      };

      if (isCreating) {
        const { error } = await supabase
          .from('license_types')
          .insert(dataToSave);

        if (error) throw error;
        toast.success('Type de licence créé avec succès');
      } else if (editingType) {
        // .select() : sans lui, une mise à jour bloquée par RLS ne renvoie ni erreur ni ligne
        const { data, error } = await supabase
          .from('license_types')
          .update(dataToSave)
          .eq('id', editingType.id)
          .select('id');

        if (error) throw error;
        if (!data?.length) throw new Error('Aucune ligne modifiée (droits insuffisants ?)');
        toast.success('Type de licence mis à jour avec succès');
      }

      resetForm();
      onSuccess?.();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }, [formData, isCreating, editingType, resetForm, onSuccess]);

  return {
    editingType,
    isCreating,
    formData,
    saving,
    setFormData,
    resetForm,
    startEdit,
    startCreate,
    handleSave
  };
}
